import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export default function Lightbox({ open, onClose, src, alt, caption }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setScale(1);
    setPos({ x: 0, y: 0 });
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el || !open) return;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((s) => {
        const next = clamp(s - e.deltaY * 0.0022, 1, 4);
        if (next === 1) setPos({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  const zoomBy = (d) =>
    setScale((s) => {
      const next = clamp(s + d, 1, 4);
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const endDrag = () => (dragRef.current = null);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinchRef.current = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const [a, b] = e.touches;
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      setScale((s) => clamp(s * (d / pinchRef.current), 1, 4));
      pinchRef.current = d;
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex flex-col bg-[#02120D]/95"
          data-testid="lightbox-modal"
          role="dialog"
          aria-modal="true"
          aria-label={caption}
        >
          <div className="flex items-center justify-between px-5 py-4 md:px-8">
            <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-[#F5F1E8]/70">
              {caption}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => zoomBy(-0.6)}
                data-testid="lightbox-zoom-out"
                aria-label="Zoom out"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F5F1E8]/25 text-[#F5F1E8] transition-colors duration-300 hover:border-[#A98B5F]"
              >
                <Minus size={15} />
              </button>
              <button
                onClick={() => zoomBy(0.6)}
                data-testid="lightbox-zoom-in"
                aria-label="Zoom in"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F5F1E8]/25 text-[#F5F1E8] transition-colors duration-300 hover:border-[#A98B5F]"
              >
                <Plus size={15} />
              </button>
              <button
                onClick={onClose}
                data-testid="lightbox-close"
                aria-label="Close"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F5F1E8]/25 text-[#F5F1E8] transition-colors duration-300 hover:border-[#A98B5F]"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div
            ref={boxRef}
            className="relative flex flex-1 items-center justify-center overflow-hidden px-4"
            style={{ touchAction: "none", cursor: scale > 1 ? "grab" : "default" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onDoubleClick={() => {
              setScale((s) => (s > 1 ? 1 : 2.2));
              setPos({ x: 0, y: 0 });
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="flex max-h-full max-w-full items-center justify-center"
            >
              <img
                src={src}
                alt={alt}
                data-testid="lightbox-image"
                draggable={false}
                className="max-h-[78vh] max-w-full select-none object-contain"
                style={{
                  transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                  transition: dragRef.current ? "none" : "transform 0.25s ease-out",
                }}
              />
            </motion.div>
          </div>
          <p className="px-6 pb-5 text-center text-[10px] uppercase tracking-[0.28em] text-[#F5F1E8]/45">
            Scroll or pinch to zoom · Drag to pan · Esc to close
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
